import {
    Car,
    Factory,
    Zap,
    CreditCard,
    Truck,
    Shield,
    Cog,
    BarChart3,
    Users,
    Workflow,
    FileCheck,
    RefreshCw,
    AlertTriangle,
    Activity,
    Landmark,
    Scale,
    Stethoscope,
    Building,
    Cloud,
    Cpu,
    Database,
    Globe,
    Server,
    Layers,
    Lock,
} from 'lucide-react';
import { BranchData } from '../types/branch';

export const branchesEn: Record<string, BranchData> = {
    automotive: {
        title: 'Automotive',
        subtitle: 'Controlled delivery and governance for highly connected supply chains with high quality and traceability requirements.',
        heroImage: '/images/automotive_hero.png',
        contextDesc: 'Automotive organizations operate in highly interconnected supply chains with stringent quality, availability, and traceability requirements. Changes often impact multiple systems (ERP, logistics, analytics, platforms) and must be rolled out in a controlled manner.',
        operationalReality: [
            'Many interfaces (suppliers, logistics, quality data), low error tolerance',
            'High pressure on stability, availability, and traceability',
            'Legacy + modernization in parallel, strict release windows',
        ],
        deliverables: [
            'Stable delivery rhythm and controlled releases across system boundaries',
            'Security and governance baselines for identities, logs, and changes',
            'Integration discipline (contracts, monitoring, error paths)',
        ],
        capabilities: [
            { icon: RefreshCw, title: 'Change Governance', desc: 'Release/rollback runbooks and dependency management' },
            { icon: Cog, title: 'Interface Contracts', desc: 'API/EDI contracts, monitoring, and incident processes' },
            { icon: FileCheck, title: 'Evidence & Compliance', desc: 'Documented approvals, change logs, and audit trails' },
            { icon: Car, title: 'Supply Chain Integration', desc: 'Supplier, logistics, and quality data integration' },
            { icon: BarChart3, title: 'Analytics', desc: 'Real-time production and quality metrics' },
            { icon: Users, title: 'Ownership Model', desc: 'Service Owner, Integration Owner, Change Owner' },
        ],
        timeline: [
            {
                phase: 'Readiness & Inventory',
                weeks: '2–4 weeks',
                items: ['System inventory', 'Integration mapping', 'Risk assessment', 'Governance plan'],
            },
            {
                phase: 'Controlled Execution',
                weeks: '6–12 weeks',
                items: ['Release management', 'Integration testing', 'Evidence collection', 'Monitoring'],
            },
            {
                phase: 'Handover & Operations',
                weeks: '2–4 weeks',
                items: ['Runbooks', 'Ownership handover', 'Checklists', 'Lessons learned'],
            },
        ],
        riskCompliance: [
            { category: 'Traceability', items: ['Documented approvals', 'Changes and logs'] },
            { category: 'Rollback', items: ['Criteria per release', 'Dependencies explicitly managed'] },
        ],
        enablement: [
            'Ownership model: Service Owner, Integration Owner, Change Owner',
            'Runbooks + handover checklists',
        ],
        nextSteps: [
            { title: 'Orientation Call', duration: '30–45 min', desc: 'Initial assessment of your situation' },
            { title: 'Assessment', duration: '2–4 weeks', desc: 'System/integration inventory + delivery plan' },
        ],
    },
    'energie-renewables': {
        title: 'Energy & Renewables',
        subtitle: 'Digital transformation for energy providers and renewable energy with focus on data quality, IT/OT convergence, and compliance.',
        heroImage: '/images/energie_hero.png',
        contextDesc: 'Energy companies face the challenge of modernizing critical infrastructure, meeting regulatory requirements, and simultaneously driving the digitalization of generation, distribution, and consumption.',
        operationalReality: [
            'SCADA/OT systems with different lifecycles than IT',
            'Critical infrastructure requirements and regulatory pressure',
            'Decentralized facilities with heterogeneous data sources',
        ],
        deliverables: [
            'IT/OT convergence with clear interface definitions',
            'Critical infrastructure-compliant security baseline',
            'Central data platform for analytics and reporting',
            'Predictive maintenance for critical assets',
        ],
        capabilities: [
            { icon: Zap, title: 'Smart Grid', desc: 'Integration of generation, distribution, and storage' },
            { icon: Cpu, title: 'IoT & Sensors', desc: 'Data collection, edge computing, and real-time analytics' },
            { icon: BarChart3, title: 'Predictive Maintenance', desc: 'Anomaly detection and proactive maintenance' },
            { icon: Shield, title: 'IT/OT Security', desc: 'Securing IT and OT networks' },
            { icon: FileCheck, title: 'Regulatory', desc: 'Critical infrastructure and industry-specific compliance' },
            { icon: Database, title: 'Data Platform', desc: 'Central data storage for analytics and reporting' },
        ],
        timeline: [
            {
                phase: 'Assessment',
                weeks: '2–3 weeks',
                items: ['Inventory', 'Technology mapping', 'Gap analysis', 'Roadmap'],
            },
            {
                phase: 'Implementation',
                weeks: '8–16 weeks',
                items: ['Integrations', 'Security baseline', 'Analytics setup', 'Testing'],
            },
            {
                phase: 'Operations',
                weeks: '2–4 weeks',
                items: ['Monitoring', 'Runbooks', 'Training', 'Handover'],
            },
        ],
        riskCompliance: [
            { category: 'Critical Infrastructure', items: ['Regulatory requirements', 'Reporting obligations', 'Audits'] },
            { category: 'IT/OT Security', items: ['Network segmentation', 'Access control', 'Anomaly detection'] },
        ],
        enablement: [
            'Cross-functional training for IT and OT teams',
            'Runbooks for incident response in IT and OT environments',
        ],
        nextSteps: [
            { title: 'IT/OT Assessment', duration: '2–3 weeks', desc: 'Analysis of system landscape and security posture' },
            { title: 'Pilot', duration: '6–8 weeks', desc: 'Proof of concept for prioritized use cases' },
        ],
    },
    'logistik-warehousing': {
        title: 'Logistics & Warehousing',
        subtitle: 'End-to-end transparency for your supply chain with focus on automation, real-time tracking, and system integration.',
        heroImage: '/images/logistik_hero.png',
        contextDesc: 'Logistics companies are under pressure to make supply chains more transparent, faster, and more efficient. We help with the integration of WMS, TMS, and ERP as well as the implementation of real-time tracking and analytics.',
        operationalReality: [
            'Heterogeneous system landscape (WMS, TMS, ERP, carrier APIs)',
            'High requirements for real-time data and SLAs',
            'Seasonal peaks with scaling requirements',
        ],
        deliverables: [
            'End-to-end transparency across the entire supply chain',
            'Seamless integration of WMS, TMS, and carrier systems',
            'Real-time tracking and predictive analytics',
            'Automation of routine processes',
        ],
        capabilities: [
            { icon: Truck, title: 'TMS Integration', desc: 'Transport management system and carrier connectivity' },
            { icon: Factory, title: 'WMS Optimization', desc: 'Warehouse management and inventory control' },
            { icon: Cog, title: 'EDI & API', desc: 'B2B integration and real-time data exchange' },
            { icon: BarChart3, title: 'Demand Forecasting', desc: 'ML-based forecasts and capacity planning' },
            { icon: Cpu, title: 'IoT & Tracking', desc: 'Sensors, RFID, and GPS tracking' },
            { icon: Shield, title: 'Compliance', desc: 'Customs, hazmat, and industry-specific requirements' },
        ],
        timeline: [
            {
                phase: 'Discovery',
                weeks: '2–3 weeks',
                items: ['Process mapping', 'System landscape', 'Pain points', 'Quick wins'],
            },
            {
                phase: 'Integration',
                weeks: '6–12 weeks',
                items: ['API development', 'WMS/TMS connectivity', 'Testing', 'Rollout'],
            },
            {
                phase: 'Optimization',
                weeks: '2–4 weeks',
                items: ['Analytics setup', 'KPI dashboards', 'Training', 'Continuous improvement'],
            },
        ],
        riskCompliance: [
            { category: 'Data Quality', items: ['Master data management', 'Validation rules'] },
            { category: 'Compliance', items: ['Customs documentation', 'Hazmat', 'Proof obligations'] },
        ],
        enablement: [
            'Training for dispatchers and warehouse teams',
            'Playbooks for peak scenarios and escalations',
        ],
        nextSteps: [
            { title: 'Supply Chain Assessment', duration: '1–2 weeks', desc: 'Analysis of current processes and systems' },
            { title: 'Integration Pilot', duration: '4–6 weeks', desc: 'PoC for prioritized interfaces' },
        ],
    },
    'healthcare-medtech': {
        title: 'Healthcare & MedTech',
        subtitle: 'Secure digital health. DiGA, KHZG, and MDR-compliant platforms.',
        heroImage: '/images/healthcare_medtech_hero.png',
        contextDesc: 'Healthcare is undergoing massive transformation. KHZG funding, DiGA fast-track, and the electronic patient record (ePA) are driving digitalization. At the same time, hurdles from GDPR, MDR, and Gematik requirements are higher than ever.',
        operationalReality: [
            'Outdated HIS systems and lack of interoperability (FHIR/HL7)',
            'High regulatory hurdles (MDR, DiGAV) slow innovation',
            'Most sensitive patient data requires highest protection level',
        ],
        deliverables: [
            'DiGA / DiPA compliance check & fast-track support',
            'FHIR interoperability layer for HIS integration',
            'Cloud platforms for health data (C5, BSI-compliant)',
            'MDR-compliant software development processes',
        ],
        capabilities: [
            { icon: Activity, title: 'Interoperability', desc: 'FHIR, HL7, Gematik connectivity and HIS integration' },
            { icon: Scale, title: 'Regulatory Affairs', desc: 'MDR, DiGAV, GDPR in healthcare' },
            { icon: Shield, title: 'Information Security', desc: 'ISO 27001, B3S Hospital, patient data protection' },
            { icon: Cloud, title: 'Health Cloud', desc: 'Secure infrastructure (HDS, C5) for health data' },
            { icon: Stethoscope, title: 'Digital Health', desc: 'Development of DiGA, DiPA, and patient apps' },
            { icon: FileCheck, title: 'Quality Management', desc: 'QMS according to ISO 13485 for software as medical device' },
        ],
        timeline: [
            {
                phase: 'Regulatory Assessment',
                weeks: '2-4 weeks',
                items: ['MDR classification', 'Data protection impact assessment', 'Gap analysis'],
                validation: ['Regulatory roadmap'],
            },
            {
                phase: 'Platform & Compliance',
                weeks: '3-6 months',
                items: ['Secure cloud architecture', 'QMS setup', 'Interoperability layer'],
                validation: ['Audit-ready status'],
            },
            {
                phase: 'Development & Certification',
                weeks: '4-8 months',
                items: ['MVP development', 'Technical documentation', 'Penetration tests'],
                validation: ['DiGA/MDR approval'],
            },
        ],
        riskCompliance: [
            { category: 'Regulatory', items: ['MDR (Medical Device Regulation)', 'DiGAV', 'GDPR', 'SGB V'] },
            { category: 'Technical', items: ['Gematik specifications', 'FHIR standards', 'BSI TR'] },
        ],
        enablement: [
            'Training on MDR-compliant software development',
            'Data protection training for medical staff',
            'Interoperability workshops (FHIR)',
        ],
        nextSteps: [
            { title: 'DiGA Check-up', duration: '2 hours', desc: 'Potential analysis for digital health applications' },
            { title: 'Interoperability Workshop', duration: '1 day', desc: 'Strategy for HIS integration and FHIR' },
        ],
    },
    'public-sector': {
        title: 'Public Sector & Administration',
        subtitle: 'Digital sovereignty for the modern state. Government digitalization and secure infrastructure.',
        heroImage: '/images/public_sector_hero.png',
        contextDesc: 'Government must go digital – secure, accessible, and sovereign. Digitalization mandates set tight deadlines. We build bridges between legacy systems and modern cloud architectures.',
        operationalReality: [
            'Heterogeneous IT landscape and specialized applications',
            'Strict data sovereignty and security requirements',
            'Skilled worker shortage slows implementation',
        ],
        deliverables: [
            'Digital government service implementation',
            'Sovereign cloud strategy (private cloud, secure providers)',
            'Security audit preparation and compliance',
            'Accessible frontend development (WCAG)',
        ],
        capabilities: [
            { icon: Landmark, title: 'Digital Sovereignty', desc: 'Strategies for digital independence and open source' },
            { icon: FileCheck, title: 'Government Services', desc: 'Implementation of digital public services' },
            { icon: Shield, title: 'Security Standards', desc: 'Security concepts according to government standards' },
            { icon: Users, title: 'Accessibility', desc: 'Accessible IT according to WCAG standards' },
            { icon: Building, title: 'Smart City', desc: 'Connected infrastructure and IoT platforms' },
            { icon: RefreshCw, title: 'Modernization', desc: 'Replacement of specialized and legacy systems' },
        ],
        timeline: [
            {
                phase: 'Needs Analysis & Concept',
                weeks: '4-8 weeks',
                items: ['Requirements catalog', 'Security concept', 'Solution architecture'],
                validation: ['Project mandate'],
            },
            {
                phase: 'Implementation & Piloting',
                weeks: '6-12 months',
                items: ['MVP development', 'Security audit prep', 'Pilot operation'],
                validation: ['Go-live pilot'],
            },
            {
                phase: 'Rollout & Operations',
                weeks: '3-6 months',
                items: ['Broad rollout', 'Operations handbook', 'Training'],
                validation: ['Acceptance'],
            },
        ],
        riskCompliance: [
            { category: 'Regulatory', items: ['Government regulations', 'Security standards', 'Accessibility', 'GDPR'] },
            { category: 'Sovereignty', items: ['Open source', 'Avoiding vendor lock-in', 'Data residency'] },
        ],
        enablement: [
            'Security standards training',
            'Accessible development workshops',
            'Agile methods in government',
        ],
        nextSteps: [
            { title: 'Digital Services Check', duration: '1 day', desc: 'Status analysis and roadmap' },
            { title: 'Sovereignty Workshop', duration: '4 hours', desc: 'Cloud strategy for government' },
        ],
    },
    fintech: {
        title: 'Fintech',
        subtitle: 'Secure, scalable technology solutions for financial services with focus on compliance, auditability, and fast releases.',
        heroImage: '/images/fintech_hero.png',
        contextDesc: 'Fintech organizations must deliver trust, availability, and traceability simultaneously. Requirements for auditability, access control, incident readiness, and vendor risk are operational necessities – not optional.',
        operationalReality: [
            'High pressure on secure releases and fast responsiveness',
            'Strict evidence requirements for access, changes, approvals, and incidents',
            'Dependency on third parties (payment, KYC, cloud), complex data flows',
        ],
        deliverables: [
            'Governance and evidence structure for changes, releases, and controls',
            'Security/privacy baseline including incident playbooks and exercises',
            'Delivery discipline: CI gates, test strategy, rollback paths, observability',
        ],
        capabilities: [
            { icon: Lock, title: 'Identity & Access', desc: 'Least privilege, MFA, admin audit' },
            { icon: BarChart3, title: 'Observability', desc: 'Central logs, alerts, SLOs' },
            { icon: Workflow, title: 'Release Controls', desc: 'Quality gates, approvals, change logs' },
            { icon: Globe, title: 'Vendor Controls', desc: 'Access matrix, evidence requirements, regular reviews' },
            { icon: CreditCard, title: 'Payment Security', desc: 'PCI-DSS, tokenization, fraud detection' },
            { icon: AlertTriangle, title: 'Incident Response', desc: 'Playbooks, escalation paths, tabletop exercises' },
        ],
        timeline: [
            {
                phase: 'Baseline & Evidence Assessment',
                weeks: '2–3 weeks',
                items: ['Controls inventory', 'Gap analysis', 'Risk assessment', 'Prioritization'],
            },
            {
                phase: 'Controls & Guardrails',
                weeks: '4–8 weeks',
                items: ['Security controls', 'CI/CD gates', 'Monitoring', 'Documentation'],
            },
            {
                phase: 'Operationalization',
                weeks: '4–8 weeks',
                items: ['Training', 'Tabletop exercise', 'Continuous monitoring', 'Review cadence'],
            },
        ],
        riskCompliance: [
            { category: 'Traceability', items: ['Approvals', 'Logs', 'Releases', 'Training records'] },
            { category: 'Incident Readiness', items: ['Escalation paths', 'Runbooks', 'Exercise formats'] },
        ],
        enablement: [
            'Ownership model: Security/Privacy Owner, Service Owner, Change Owner',
            'Runbooks + checklists for operations and changes',
        ],
        nextSteps: [
            { title: 'Evidence/Control Baseline Call', duration: '60–90 min', desc: 'Initial assessment of your controls' },
            { title: 'Assessment', duration: '2–3 weeks', desc: 'Controls + evidence + implementation plan' },
        ],
    },
    'fertigung-iiot': {
        title: 'Manufacturing & Industrial IoT',
        subtitle: 'Production digitalization with OT/IT integration, predictive maintenance, and data-driven optimization.',
        heroImage: '/images/fertigung_hero.png',
        contextDesc: 'Manufacturing companies face the challenge of leveraging production data without compromising the stability and security of the OT environment. We help with secure IT/OT integration and building analytics platforms.',
        operationalReality: [
            'OT systems with long lifecycles and limited update capabilities',
            'Siloed data in machines, SCADA, and MES',
            'Focus on availability and production safety',
        ],
        deliverables: [
            'Secure IT/OT integration without production risk',
            'Central data platform for production analytics',
            'Predictive maintenance for critical assets',
            'OEE optimization through data-driven insights',
        ],
        capabilities: [
            { icon: Cpu, title: 'Sensor Integration', desc: 'Connection of PLCs, SCADA, and sensors' },
            { icon: Server, title: 'Edge Computing', desc: 'Local data processing and latency minimization' },
            { icon: Database, title: 'Data Lake', desc: 'Central data storage for analytics' },
            { icon: BarChart3, title: 'Manufacturing Analytics', desc: 'OEE, quality metrics, and trend analysis' },
            { icon: Shield, title: 'OT Security', desc: 'Securing production networks' },
            { icon: Layers, title: 'MES Integration', desc: 'Manufacturing Execution System connectivity' },
        ],
        timeline: [
            {
                phase: 'Assessment',
                weeks: '2–3 weeks',
                items: ['Asset inventory', 'Data source mapping', 'Security review', 'Use case prioritization'],
            },
            {
                phase: 'Pilot',
                weeks: '6–10 weeks',
                items: ['Sensor integration', 'Edge setup', 'Analytics MVP', 'Validation'],
            },
            {
                phase: 'Scale',
                weeks: '4–12 weeks',
                items: ['Rollout', 'Training', 'Monitoring', 'Continuous improvement'],
            },
        ],
        riskCompliance: [
            { category: 'OT Security', items: ['Network segmentation', 'Anomaly detection', 'Access control'] },
            { category: 'Availability', items: ['Failsafe mechanisms', 'Rollback plans', 'Testing in non-production hours'] },
        ],
        enablement: [
            'Cross-functional training for IT and OT teams',
            'Playbooks for incident response and maintenance',
        ],
        nextSteps: [
            { title: 'IIoT Readiness Assessment', duration: '2–3 weeks', desc: 'Analysis of production landscape and use cases' },
            { title: 'Pilot', duration: '6–8 weeks', desc: 'PoC for prioritized assets' },
        ],
    },
};
